import { useState } from 'react';
import { Button, Modal, Select } from 'antd';
import { useTheme } from '../../providers/theme';
import { DownloadOutlined } from '@ant-design/icons';
import { getColors } from '../utils/getColors';
import { useSelected } from '../../providers/selectedContext';
import { AnalyzeType, Curves } from '../../@types/projects/types';
import {
  buildLambdaZoneCurves,
  buildLiaoZoneCurves,
  clampCurvePointCount,
  DEFAULT_RESULTS_CURVE_POINTS
} from '../utils/curveGeneration';

const toEuropeanDecimal = (value: number, decimals: number = 5): string => {
  return value.toFixed(decimals).replace('.', ',');
};

const formatCurveRows = (
  sw: number[],
  krw: number[],
  krow: number[],
  pcow: number[]
): string => {
  const len = Math.min(sw.length, krw.length, krow.length, pcow.length);
  const rows: string[] = [];
  for (let i = 0; i < len; i++) {
    rows.push(
      `${toEuropeanDecimal(sw[i])}\t${toEuropeanDecimal(krw[i])}\t${toEuropeanDecimal(krow[i])}\t${toEuropeanDecimal(pcow[i])}`
    );
  }
  return rows.join('\n');
};

const formatGasCurveRows = (
  sg: number[],
  krg: number[],
  krog: number[]
): string => {
  const len = Math.min(sg.length, krg.length, krog.length);
  const rows: string[] = [];
  for (let i = 0; i < len; i++) {
    rows.push(
      `${toEuropeanDecimal(sg[i])}\t${toEuropeanDecimal(krg[i])}\t${toEuropeanDecimal(krog[i])}\t${toEuropeanDecimal(0)}`
    );
  }
  return rows.join('\n');
};

const buildPetrelContent = (curves: Curves, scaled: boolean): string => {
  const krw = scaled ? curves.krw_scaled : curves.krw;
  const krow = scaled ? curves.krow_scaled : curves.krow;
  const krg = scaled ? curves.krg_scaled : curves.krg;
  const krog = scaled ? curves.krog_scaled : curves.krog;

  const swofRows = formatCurveRows(curves.sw, krw, krow, curves.pcow);
  const sgofRows = formatGasCurveRows(curves.sg, krg, krog);

  return (
    `SWOF\n--sw\tKrw\tKro\tPcow (Psia)\n${swofRows}\n/\n\n` +
    `SGOF\n--sg\tKrg\tKrog\tPcog (Psia)\n${sgofRows}\n/\n\n`
  );
};

const ExportResults = () => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo } = useSelected();

  const [modalOpen, setModalOpen] = useState(false);
  const [exportType, setExportType] = useState<AnalyzeType>(AnalyzeType.LIAO);

  const handleExport = () => {
    const zones = selectedInfo.project?.correlation?.zones;
    if (!zones?.length) return;
    setModalOpen(true);
  };

  const doExport = () => {
    const zones = selectedInfo.project?.correlation?.zones;
    const gradDiff = selectedInfo.project?.correlation?.grad_diff;
    if (!zones?.length) return;

    const scaled = localStorage.getItem('showScaledKr') === 'true';
    const rawPointCount = selectedInfo.project?.uid
      ? localStorage.getItem(`resultsNumberOfPoints_${selectedInfo.project.uid}`)
      : null;
    const pointCount = clampCurvePointCount(
      rawPointCount == null ? DEFAULT_RESULTS_CURVE_POINTS : Number(rawPointCount),
      DEFAULT_RESULTS_CURVE_POINTS
    );

    let content = '';
    for (const zone of zones) {
      const curves = typeof gradDiff === 'number'
        ? exportType === AnalyzeType.LIAO
          ? buildLiaoZoneCurves(zone, gradDiff, pointCount)
          : buildLambdaZoneCurves(zone, gradDiff, pointCount)
        : null;

      if (!curves) continue;

      content += buildPetrelContent(curves, scaled);
    }

    if (!content) return;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'results.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    setModalOpen(false);
  };

  return (
    <>
      <Button
        data-testid="export-results-button"
        onClick={handleExport}
        style={{
          color: colors.text,
          display: 'flex',
          alignItems: 'center',
          border: `1px solid ${colors.border}`,
          gap: '0.5rem',
          borderRadius: '7px',
          padding: '1.05rem 1rem'
        }}
      >
        <DownloadOutlined />
        Export Data
      </Button>
      <Modal
        title="Select export method"
        open={modalOpen}
        onOk={doExport}
        onCancel={() => setModalOpen(false)}
        okText="Export"
      >
        <Select
          value={exportType}
          onChange={setExportType}
          options={[
            { value: AnalyzeType.LIAO, label: 'Liao' },
            { value: AnalyzeType.LAMBDA, label: 'Brooks-Corey' }
          ]}
          style={{ width: '100%', marginTop: '0.5rem' }}
        />
      </Modal>
    </>
  );
};

export default ExportResults;
